/**
 * voice-v8 — Werkzeugkarten: System: Rückfrage (ask_confirm), geplante Aufgabe, Textblock, Shell, Nutzer-Lage.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { absatz, aktionsKnopf, aktionsLeiste, baueKarte, kartenTitel, kuerzeListe, leerHinweis, liste, listenEintrag, weitereHinweis } from "../karte.js";
import { fuerMenschen } from "../karten-text.js";
import { gesteuert } from "../ruckel-schalter.js";
import { unwrapResult } from "./hilfe.js";

// 2026-07-04 (Martin): schedule_task — geplanter Auftrag als ruhige Card
// (statt Roh-Key-Value-Dump). Nutzt das v-card-Chip-Vokabular.
interface ScheduleShape {
  title?: string;
  erster_lauf?: string;
  wiederkehrend?: string;
  speak?: string;
}
export function buildScheduleTaskCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as ScheduleShape;
  const el = baueKarte("schedule");

  const title = document.createElement("div");
  title.className = "tool-schedule-title";
  title.textContent = r.title || "Geplanter Auftrag";
  el.appendChild(title);

  const chips = document.createElement("div");
  chips.className = "v-chips";
  if (r.erster_lauf) {
    const c1 = document.createElement("span");
    c1.className = "v-chip";
    c1.textContent = `ab ${r.erster_lauf}`;
    chips.appendChild(c1);
  }
  if (r.wiederkehrend) {
    const c2 = document.createElement("span");
    c2.className = "v-chip v-chip--soft";
    c2.textContent = r.wiederkehrend;
    chips.appendChild(c2);
  }
  el.appendChild(chips);

  const hint = document.createElement("div");
  hint.className = "tool-browser-detail";
  hint.textContent = "Läuft im Hintergrund — Ergebnis kommt über die Glocke.";
  el.appendChild(hint);
  return el;
}

// Ticket 231: ask_confirm — klickbare Ja/Nein-/Options-Buttons. Ein Klick
// schickt das Label als naechste User-Nachricht (etablierter Rueckkanal
// window.__voiceV8SendFromCard) — kein Sprach-/STT-Umweg.
interface ConfirmShape {
  question?: string;
  options?: string[];
}

/**
 * Die Frage einer Rückfrage-Karte, leer, wenn keine da ist. (Ticket 2660)
 *
 * EINE Lesart für die Karte im Verlauf und die angeheftete Karte oben
 * (rueckfrage-oben.ts) — sonst könnten oben und unten verschiedene Fragen
 * stehen.
 */
export function bestaetigungsFrage(result: unknown): string {
  const r = unwrapResult(result) as ConfirmShape;
  return typeof r.question === "string" ? r.question.trim() : "";
}

/**
 * Wer die Antwortknöpfe einer Rückfrage-Karte zeigt. (pre.3598)
 *
 * Eine Rückfrage hat EINE Stelle mit Ja/Nein, nie zwei:
 *   - `frei`: die Karte im Verlauf trägt die Knöpfe (Grundzustand, auch wenn
 *     die Karte oben weggetippt wurde oder Martin anders geantwortet hat),
 *   - `oben`: die angeheftete Karte (rueckfrage-oben.ts) trägt sie — hier
 *     steht nur eine ruhige Zeile „Offen“,
 *   - `beantwortet`: gewählt; die Zeile nennt die Wahl, die Knöpfe sind aus.
 * Die Knöpfe bleiben in jedem Zustand im Baum: die Spiegel oben drücken sie
 * (ein Weg, eine Antwort zu senden), versteckt sind sie für Tastatur und
 * Screenreader aus der Bedienung genommen (`hidden`).
 */
export type ConfirmStand = "frei" | "oben" | "beantwortet";

// 15.10.2026 (Martin: „komische … Buttons“): die Zeile sagt, was Martin tun kann — nicht, wo ein Knopf wohnt.
const STAND_TEXT: Record<Exclude<ConfirmStand, "beantwortet">, string> = {
  frei: "",
  oben: "Offen – antworte oben in der Rückfrage.",
};

/** Die Rückfrage-Karte im Verlauf, zu der ein Knopf gehört — sonst `null`. */
export function confirmKarteVon(knopf: Element | undefined): HTMLElement | null {
  return knopf?.closest<HTMLElement>(".tool-confirm") ?? null;
}

/** Den Stand einer Rückfrage-Karte setzen; eine beantwortete bleibt beantwortet. */
export function setzeConfirmStand(
  el: HTMLElement | null,
  stand: ConfirmStand,
  antwort = "",
): void {
  if (!el || (el.dataset.stand === "beantwortet" && stand !== "beantwortet")) return;
  const zeile = el.querySelector<HTMLElement>(".tool-confirm-stand");
  const knoepfe = el.querySelector<HTMLElement>(".tool-actions");
  el.dataset.stand = stand;
  const text =
    stand === "beantwortet" ? (antwort ? `Beantwortet: ${antwort}` : "Beantwortet") : STAND_TEXT[stand];
  if (zeile) {
    zeile.textContent = text;
    zeile.hidden = !text;
  }
  if (knoepfe) knoepfe.hidden = stand !== "frei";
}

/**
 * Eine Rückfrage-Karte als beantwortet zeigen: Knöpfe aus, die Wahl markiert,
 * die Karte ruhig. Der Klick und der Abgleich mit dem Server gehen denselben
 * Weg — nach dem Neuladen sieht die Karte auf jedem Gerät gleich aus.
 *
 * @param antwort Der Wortlaut der Antwort; passt er zu einem Knopf, ist der
 *   markiert, sonst steht die Karte nur als beantwortet da.
 */
export function beantworteConfirmKarte(el: HTMLElement, antwort: string): void {
  const wort = antwort.trim().toLowerCase();
  const knoepfe = Array.from(el.querySelectorAll<HTMLButtonElement>(".tool-actions button"));
  const gewaehlt = knoepfe.find((k) => (k.textContent ?? "").trim().toLowerCase() === wort);
  for (const k of knoepfe) k.disabled = true;
  gewaehlt?.classList.add("chosen");
  el.classList.add("confirm-answered");
  setzeConfirmStand(el, "beantwortet", gewaehlt?.textContent ?? "");
}

export function buildConfirmCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as ConfirmShape;
  const el = baueKarte("confirm");
  el.dataset.stand = "frei";
  const q = document.createElement("div");
  q.className = "tool-confirm-question";
  // 2026-09-04: Die Frage geht mit. Ohne sie war „Ja" auf den gespeicherten
  // Verlauf angewiesen — und der kann beim Klick noch fehlen (siehe
  // sendUserMessageFromCard).
  const frage = bestaetigungsFrage(result);
  // Die Frage ist an Martin gerichtet — trägt sie dennoch einen Pfad oder einen Satz an das Modell, fällt der weg.
  q.textContent = fuerMenschen(frage, "Bestätigen?");
  el.appendChild(q);
  // pre.3598: die ruhige Zeile für „offen oben“ und „beantwortet“.
  const stand = document.createElement("p");
  stand.className = "tool-confirm-stand";
  stand.hidden = true;
  el.appendChild(stand);

  const opts =
    Array.isArray(r.options) && r.options.length
      ? r.options.slice(0, 4).map((o) => String(o))
      : ["Ja", "Nein"];
  const row = document.createElement("div");
  row.className = "tool-actions";
  const send = (
    window as unknown as {
      __voiceV8SendFromCard?: (text: string, bezug?: string) => Promise<void>;
    }
  ).__voiceV8SendFromCard;
  const buttons: HTMLButtonElement[] = [];
  opts.forEach((opt, i) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = i === 0 ? "tool-action-btn primary" : "tool-action-btn";
    b.textContent = opt.slice(0, 40);
    b.addEventListener("click", () => {
      if (b.disabled) return;
      // Erst sperren, dann senden: ein zweiter Tipp (oder der Spiegel oben)
      // findet nur noch gesperrte Knöpfe — die Antwort geht genau einmal.
      beantworteConfirmKarte(el, opt);
      if (send) void send(opt, frage);
    });
    buttons.push(b);
    row.appendChild(b);
  });
  el.appendChild(row);
  return el;
}

// Ticket 232: deliver_text — fertiger Liefertext (Brief/Nachricht/Entwurf)
// als briefartige Card mit Kopieren-Button, klar abgesetzt vom Gespraechstext.
interface TextBlockShape {
  title?: string | null;
  text?: string;
  ok?: boolean;
  error?: string;
}

/**
 * Langer Inhalt in einer Chat-Karte: gefaltet statt mit eigenem Rollbalken.
 *
 * ⚠️ 2026-08-30 (Martin, ueber die Kopieren-Karte): *„Dann springt, wenn ich
 * da drueber scrolle, ploetzlich das Bild nach unten und laesst nicht zart
 * ein weiteres Scrollen zu."*
 *
 * Die Ursache war `max-height: 440px; overflow-y: auto` auf dem Textkasten.
 * Ein rollbarer Kasten INNERHALB einer rollenden Seite nimmt die Geste an
 * sich; ist er am Ende, reicht der Browser sie ruckartig an die Seite weiter
 * (scroll chaining). Auf dem Finger fuehlt sich das genau so an, wie er es
 * beschreibt: erst klemmt es, dann springt es.
 *
 * ⚠️ Deshalb hier `overflow: hidden` und NICHT `auto` mit
 * `overscroll-behavior: contain`. Letzteres nimmt nur den Sprung, das Klemmen
 * bleibt. Eine Karte im Gespraechsfluss soll gar keine zweite Rollflaeche
 * sein — sie ist entweder kurz, oder man klappt sie auf.
 */
function faltbar(inhalt: HTMLElement, zeilen = 14): HTMLElement {
  const huelle = document.createElement("div");
  huelle.className = "karte-faltung";
  huelle.style.setProperty("--falt-zeilen", String(zeilen));
  inhalt.classList.add("karte-faltung__inhalt");
  huelle.appendChild(inhalt);

  const knopf = document.createElement("button");
  knopf.type = "button";
  knopf.className = "karte-faltung__knopf";
  knopf.textContent = "Ganz anzeigen";
  knopf.addEventListener("click", () => {
    const offen = huelle.classList.toggle("karte-faltung--offen");
    knopf.textContent = offen ? "Weniger anzeigen" : "Ganz anzeigen";
  });
  huelle.appendChild(knopf);

  /*
   * Der Knopf erscheint nur, wenn wirklich etwas verdeckt ist — sonst traegt
   * auch ein Dreizeiler ein „Ganz anzeigen", das nichts tut.
   *
   * ⚠️ 2026-08-30, im Browser gemessen und dabei den eigenen Fehler gefunden:
   * mit `requestAnimationFrame` stand die Karte noch nicht im Blatt. Gemessen
   * wurde 0 gegen 0, also „nichts verdeckt" — der Knopf verschwand, WAEHREND
   * der Deckel griff. Ergebnis: abgeschnittener Text ohne Weg zum Rest. Das
   * ist schlimmer als der Rollbalken, den ich ersetzt habe.
   *
   * Ein Rahmen ist kein Zeitpunkt, den man kennen kann. Der ResizeObserver
   * misst dann, wenn es wirklich eine Groesse gibt — und erneut, wenn sich
   * die Karte spaeter aendert (Schrift geladen, Fenster gedreht).
   */
  const pruefe = (): void => {
    if (huelle.classList.contains("karte-faltung--offen")) return;
    const verdeckt = inhalt.scrollHeight > inhalt.clientHeight + 4;
    huelle.classList.toggle("karte-faltung--kurz", !verdeckt);
  };
  if (typeof ResizeObserver === "function") {
    new ResizeObserver(gesteuert("wachen", pruefe, true)).observe(inhalt);
  } else {
    requestAnimationFrame(pruefe);
  }
  return huelle;
}

export function buildTextBlockCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as TextBlockShape;
  const el = baueKarte("textblock");
  const text = (r.text || "").trim();
  // 2026-07-14 (Martin, "leeres Feld — woher?"): das Tool weist leeren Text
  // mit ok:false ab, die Karte malte trotzdem leeres Feld + Kopieren. Ehrliche
  // Fehlerdarstellung statt Leer-Karte; Kopieren nur mit Inhalt.
  if (r.ok === false || !text) {
        el.appendChild(absatz(r.error === "text fehlt" || !text
        ? "Kein Liefertext übergeben (Tool-Fehlgriff) — bitte erneut formulieren lassen."
        : String(r.error ?? "Liefertext fehlgeschlagen."), "warn"));
    return el;
  }
  if (r.title) {
    const h = document.createElement("div");
    h.className = "tool-textblock-title";
    h.textContent = String(r.title);
    el.appendChild(h);
  }
  const body = document.createElement("div");
  body.className = "tool-textblock-body";
  body.textContent = text;
  el.appendChild(faltbar(body));

  const row = aktionsLeiste();
  const copy = aktionsKnopf({ text: "Kopieren", primaer: true });
  copy.addEventListener("click", () => {
    const done = (ok: boolean): void => {
      copy.textContent = ok ? "Kopiert \u2713" : "Kopieren ging nicht";
      window.setTimeout(() => {
        copy.textContent = "Kopieren";
      }, 1800);
    };
    try {
      if (navigator.clipboard?.writeText) {
        void navigator.clipboard
          .writeText(text)
          .then(() => done(true))
          .catch(() => done(false));
      } else {
        // Fallback ohne Clipboard-API.
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.className = "visually-hidden-input";
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);
        done(ok);
      }
    } catch {
      done(false);
    }
  });
  row.appendChild(copy);
  el.appendChild(row);
  return el;
}

// --- Lage je Nutzer (Ticket 2424) ---------------------------------------

interface LageTicket {
  id?: number;
  titel?: string;
  status?: string;
  seit?: string;
}

interface LageFehler {
  werkzeug?: string;
  grund?: string | null;
  anzahl?: number;
  zuletzt?: string;
}

/** Eine Zwischenueberschrift in der Karte — vorhandene Titel-Klasse. */
function lageAbschnitt(el: HTMLElement, text: string): void {
  const h = document.createElement("p");
  h.className = "tool-dr-label";
  h.textContent = text;
  el.appendChild(h);
}

/**
 * Martins Tabelle: was bei einem Nutzer offen ist, und was erledigt wurde.
 *
 * ⚠️ Die Reihenfolge ist Absicht. Zuerst, was NICHT geht (danach war
 *    gefragt), dann das Erledigte — Martin wollte beides auf einem Blatt:
 *    *„dass ich das sehe und auch sehe, was abgearbeitet wurde."*
 */
export function buildNutzerLageCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("nutzer-lage");
  const nutzer = (r.nutzer ?? {}) as { id?: number; name?: string | null };
  const name = nutzer.name ?? (nutzer.id ? `Nutzer ${nutzer.id}` : "Nutzer");
  el.appendChild(kartenTitel(`Lage · ${name}`));

  const offen = (r.offen as LageTicket[] | undefined) ?? [];
  const erledigt = (r.erledigt as LageTicket[] | undefined) ?? [];
  const fehler = (r.fehler as LageFehler[] | undefined) ?? [];
  const ohneGrund = Number(r.fehlerOhneGrund ?? 0);
  const uebersprungen = Number(r.uebersprungen ?? 0);

  if (offen.length === 0 && erledigt.length === 0 && fehler.length === 0) {
    leerHinweis(el, `Zu ${name} liegt nichts vor.`);
    return el;
  }

  if (fehler.length > 0) {
    lageAbschnitt(el, "Was nicht geht");
    const ul = liste(el);
    const g = kuerzeListe(fehler, 8);
    for (const f of g.sichtbar) {
      const mal = Number(f.anzahl ?? 1);
      ul.appendChild(
        listenEintrag(
          f.grund ?? "Grund nicht festgehalten",
          f.werkzeug,
          mal > 1 ? `${mal} mal` : undefined,
        ),
      );
    }
    weitereHinweis(el, g.rest);
    /*
     * ⚠️ Eine Luecke wird BENANNT, nicht verschwiegen. Vor pre.3071 trug
     *    ein geworfener Fehler keinen Grund; eine Liste, die das nicht
     *    sagt, sieht vollstaendig aus und ist es nicht.
     */
    if (ohneGrund > 0) {
      const p = document.createElement("p");
      p.className = "tool-card-weitere";
      p.textContent = `${ohneGrund} davon ohne festgehaltenen Grund`;
      el.appendChild(p);
    }
  }

  if (offen.length > 0) {
    lageAbschnitt(el, `Offen (${Number(r.offenGesamt ?? offen.length)})`);
    const ul = liste(el);
    const g = kuerzeListe(offen, 8);
    for (const t of g.sichtbar) {
      ul.appendChild(
        listenEintrag(t.titel ?? `Ticket ${t.id}`, t.id ? `#${t.id}` : undefined),
      );
    }
    weitereHinweis(el, g.rest);
  }

  if (erledigt.length > 0) {
    lageAbschnitt(el, `Erledigt (${Number(r.erledigtGesamt ?? erledigt.length)})`);
    const ul = liste(el);
    const g = kuerzeListe(erledigt, 8);
    for (const t of g.sichtbar) {
      ul.appendChild(
        listenEintrag(t.titel ?? `Ticket ${t.id}`, t.id ? `#${t.id}` : undefined),
      );
    }
    weitereHinweis(el, g.rest);
  }

  /*
   * ⚠️ Die uebersprungenen Tickets sagen nichts ueber den Nutzer, sondern
   *    ueber die Maschine: sie legt automatisch an und verwirft selbst.
   *    Darum stehen sie unten und werden nicht zu den Erledigten gezaehlt.
   */
  if (uebersprungen > 0) {
    const p = document.createElement("p");
    p.className = "tool-card-weitere";
    p.textContent = `${uebersprungen} automatische Meldungen wurden übersprungen`;
    el.appendChild(p);
  }
  return el;
}

// --- Projects ------------------------------------------------------------

// Pre.168.4: shell_exec Card — formatierte Anzeige statt Raw-JSON.
// Header mit Befehl (mono) + Status-Pill, Body als preformatiertes
// Stdout (max 600px hoch, scrollbar). Stderr nur wenn vorhanden.
interface ShellExecResult {
  ok?: boolean;
  exitCode?: number;
  stdout?: string;
  stderr?: string;
  command?: string;
  cwd?: string;
  durationMs?: number;
  error?: string;
}

export function buildShellExecCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as unknown as ShellExecResult;
  const el = baueKarte("shell-exec");

  const head = document.createElement("div");
  head.className = "shell-exec-head";
  const badge = document.createElement("span");
  badge.className = "shell-exec-badge";
  badge.textContent = "$ shell";
  head.appendChild(badge);
  const cmd = document.createElement("code");
  cmd.className = "shell-exec-cmd";
  cmd.textContent = r.command ?? "(kein Befehl)";
  head.appendChild(cmd);
  const status = document.createElement("span");
  status.className = "shell-exec-status";
  status.dataset.ok = String(r.ok === true);
  if (r.ok) {
    status.textContent = `exit 0 · ${r.durationMs ?? 0}ms`;
  } else if (typeof r.exitCode === "number" && r.exitCode !== 0) {
    status.textContent = `exit ${r.exitCode}`;
  } else if (r.error) {
    status.textContent = String(r.error).slice(0, 60);
  } else {
    status.textContent = "fehler";
  }
  head.appendChild(status);
  el.appendChild(head);

  const stdout = (r.stdout ?? "").toString();
  if (stdout.trim()) {
    const pre = document.createElement("pre");
    pre.className = "shell-exec-stdout";
    pre.textContent = stdout;
    el.appendChild(pre);
  }
  const stderr = (r.stderr ?? "").toString();
  if (stderr.trim()) {
    const pre = document.createElement("pre");
    pre.className = "shell-exec-stderr";
    pre.textContent = stderr;
    el.appendChild(pre);
  }

  return el;
}
